import React, { useEffect, useState } from 'react';
import { Intent, intentLabel } from '../../types/discipline';
import './ContractQuantityModal.css';
import { fmtPlainDate } from '../../utils/plainDate';

/** The fills the user confirms (gold v2 phase 3b): prefilled with the last
 *  trade / the Price, saved as entered — never silently the screener price. */
export interface ConfirmedFills {
  premium: number;
  stockPrice: number | null;
  buyback: number | null;
}

interface ContractQuantityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (numContracts: number, intent: Intent, fills: ConfirmedFills) => void;
  ticker: string;
  strikePrice: number;
  expirationDate: string;
  premium: number;
  isLoading?: boolean;
  isRollMode?: boolean;
  isWheelMode?: boolean;  // BAB-34: selling a covered call on shares assigned from a CSP
  nextEarningsDate?: string | null;
  /** From the backend (gold.v_option_chain): earnings on or before this expiry, and the days to them (New York date). */
  earningsInside?: boolean | null;
  daysToEarnings?: number | null;
  contractType?: 'call' | 'put';  // BAB-28: 'put' = cash-secured put
  /** Prefill for the stock fill (the Price shown on the page). */
  stockPrice?: number | null;
  /** Roll mode: prefill for the buy-back fill (the current option's last trade). */
  buybackPrice?: number | null;
}

const ContractQuantityModal: React.FC<ContractQuantityModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  ticker,
  strikePrice,
  expirationDate,
  premium,
  isLoading = false,
  isRollMode = false,
  isWheelMode = false,
  nextEarningsDate = null,
  earningsInside = null,
  daysToEarnings = null,
  contractType = 'call',
  stockPrice = null,
  buybackPrice = null,
}) => {
  const [numContracts, setNumContracts] = useState<number>(1);
  const fmt2 = (v: number | null | undefined) => (v != null && Number.isFinite(v) ? v.toFixed(2) : '');
  const [premiumFill, setPremiumFill] = useState(fmt2(premium));
  const [stockFill, setStockFill] = useState(fmt2(stockPrice));
  const [buybackFill, setBuybackFill] = useState(fmt2(buybackPrice));
  useEffect(() => { if (isOpen) { setPremiumFill(fmt2(premium)); setStockFill(fmt2(stockPrice)); setBuybackFill(fmt2(buybackPrice)); } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, premium, stockPrice, buybackPrice]);
  // BAB-71: the user picks their Discipline intent up front. Pre-select the
  // strategy-appropriate stance (CC → Keep My Shares, the conservative default;
  // CSP → Harvest Premium; wheel turns are 'wheel' by definition) — always
  // user-changeable. Only shown on a fresh add; rolls carry the prior leg's intent.
  const defaultIntent: Intent = isWheelMode ? 'wheel' : contractType === 'put' ? 'release' : 'hold';
  const [intent, setIntent] = useState<Intent>(defaultIntent);
  const showIntentPicker = !isRollMode && !isWheelMode;

  if (!isOpen) return null;

  const isCsp = contractType === 'put';

  // Plain-English chooser copy (one stance per row). The user owns this — it is
  // their rule, never our recommendation.
  // v3.1: a put has two intents ("Stay in Cash" is Harvest Premium).
  const INTENT_OPTIONS: { value: Intent; label: string; blurb: string }[] = [
    ...(isCsp ? [] : [{ value: 'hold' as Intent, label: intentLabel('hold', isCsp), blurb: 'Keep the shares no matter what: roll the call early, or buy it back. Never assigned, never sold.' }]),
    { value: 'wheel',   label: 'Cycle for Income', blurb: isCsp ? 'Fine to buy the shares at this strike, then sell calls on them (the Wheel).' : 'Fine being assigned at or above your net basis, then keep cycling premium (the Wheel).' },
    { value: 'release', label: 'Harvest Premium',  blurb: isCsp ? 'Just here for the premium: take profits early, never take the shares.' : 'Just here for the premium: take profits early, close before assignment.' },
  ];

  // Earnings inside the contract window (today → expiry) is the core covered-call
  // trap: the call is still open when the stock gaps on the print.
  // The backend's one test (glossary earnings_inside) and day count; formatting only here.
  const earningsInWindow = earningsInside && daysToEarnings != null && nextEarningsDate
    ? { days: daysToEarnings, label: fmtPlainDate(nextEarningsDate) ?? nextEarningsDate }
    : null;

  const totalShares = numContracts * 100;
  const totalPremium = premium * numContracts * 100;
  // CSP: the broker secures cash = strike × 100 × contracts (covers assignment).
  const cashToSecure = strikePrice * numContracts * 100;

  const numOrNull = (v: string) => (v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : null);
  const premiumNum = numOrNull(premiumFill);
  const stockNum = numOrNull(stockFill);
  const buybackNum = numOrNull(buybackFill);
  // A covered call records what the shares cost; a roll records the buy-back.
  const needsStock = !isCsp && !isWheelMode;
  const fillsOk = premiumNum != null && premiumNum > 0 && (!needsStock || (stockNum != null && stockNum > 0))
    && (!isRollMode || buybackNum != null);

  const handleConfirm = () => {
    if (!fillsOk) return;
    onConfirm(numContracts, intent, { premium: premiumNum as number, stockPrice: stockNum, buyback: buybackNum });
    setNumContracts(1); // Reset for next use
    setIntent(defaultIntent);
  };

  const handleClose = () => {
    setNumContracts(1); // Reset for next use
    setIntent(defaultIntent);
    onClose();
  };

  const money = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const title = isWheelMode ? 'Continue the Wheel' : isRollMode ? 'Roll position' : 'Add to portfolio';

  return (
    <div className="cqm-overlay" onClick={() => !isLoading && handleClose()}>
      <div className="cqm" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        {/* Header: ticket-style contract line */}
        <div className="cqm-head">
          <div>
            <h2 className="cqm-title">{title}</h2>
            <div className="cqm-contract">
              <b>{ticker}</b>
              <span className="cqm-badge">{isCsp ? 'CSP' : 'CC'}</span>
              <span className="cqm-dim">{strikePrice.toFixed(2)} {isCsp ? 'put' : 'call'} · {expirationDate}</span>
            </div>
          </div>
          <button type="button" className="cqm-x" onClick={handleClose} disabled={isLoading} aria-label="Close">×</button>
        </div>

        <div className="cqm-body">
          {/* Earnings-before-expiry warning — the core covered-call trap */}
          {earningsInWindow && (
            <div className={`cqm-warn${earningsInWindow.days <= 2 ? ' is-urgent' : ''}`}>
              <b>Earnings before this expiry.</b> {ticker} reports {earningsInWindow.label}
              {' '}({earningsInWindow.days === 0 ? 'today' : earningsInWindow.days === 1 ? 'tomorrow' : `in ${earningsInWindow.days} days`}),
              {' '}before the {expirationDate} expiry. This {isCsp ? 'put' : 'call'} stays open through the print: {isCsp ? 'gap-down assignment' : 'gap'} and IV-crush risk.
            </div>
          )}

          {/* Contracts */}
          <div className="cqm-sec">
            <label className="cqm-h" htmlFor="cqm-qty">Contracts</label>
            <div className="cqm-stepper">
              <button type="button" onClick={() => setNumContracts(Math.max(1, numContracts - 1))} disabled={isLoading} aria-label="One fewer contract">−</button>
              <input
                id="cqm-qty"
                type="number"
                min="1"
                max="100"
                value={numContracts}
                disabled={isLoading}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  setNumContracts(Math.min(100, Math.max(1, val)));
                }}
              />
              <button type="button" onClick={() => setNumContracts(Math.min(100, numContracts + 1))} disabled={isLoading} aria-label="One more contract">+</button>
            </div>
          </div>

          {/* Intent picker — BAB-71. The user sets their own stance up front so the
              Discipline rules that apply to this position are never mistaken for an
              app recommendation. Hidden for rolls (carry-forward) and wheel turns. */}
          {showIntentPicker && (
            <div className="cqm-sec">
              <div className="cqm-h">Your rule for this trade</div>
              <p className="cqm-note">Sets which Discipline rules watch this position. Change it anytime on the position.</p>
              <div className="cqm-intents" role="radiogroup" aria-label="Your rule for this trade">
                {INTENT_OPTIONS.map((opt) => {
                  const active = intent === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`cqm-intent${active ? ' is-on' : ''}`}
                      onClick={() => !isLoading && setIntent(opt.value)}
                      disabled={isLoading}
                    >
                      <span className="cqm-radio" aria-hidden="true" />
                      <span>
                        <b>{opt.label}</b>
                        <small>{opt.blurb}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Your fills: prefilled with the last trade and the Price, saved as entered */}
          <div className="cqm-sec">
            <div className="cqm-h">Your fill</div>
            <p className="cqm-note">Prefilled with the last trade{needsStock ? ' and the Price' : ''}. Enter what you actually got: it is what Babylon records.</p>
            <div className="cqm-fill">
              <label htmlFor="cqm-premium">{isCsp ? 'Put' : 'Call'} sold at</label>
              <input id="cqm-premium" type="number" min="0" step="0.01" inputMode="decimal" value={premiumFill} disabled={isLoading} onChange={(e) => setPremiumFill(e.target.value)} />
              {needsStock && (<>
                <label htmlFor="cqm-stock">{isRollMode ? 'Stock price now' : 'Shares bought at'}</label>
                <input id="cqm-stock" type="number" min="0" step="0.01" inputMode="decimal" value={stockFill} disabled={isLoading} onChange={(e) => setStockFill(e.target.value)} />
              </>)}
              {isRollMode && (<>
                <label htmlFor="cqm-buyback">Old option bought back at</label>
                <input id="cqm-buyback" type="number" min="0" step="0.01" inputMode="decimal" value={buybackFill} disabled={isLoading} onChange={(e) => setBuybackFill(e.target.value)} />
              </>)}
            </div>
          </div>

          {/* Order summary */}
          <dl className="cqm-sum">
            <div><dt>{isCsp ? 'Cash to secure' : 'Shares covered'}</dt><dd>{isCsp ? money(cashToSecure) : `${totalShares.toLocaleString()} sh`}</dd></div>
            <div><dt>Premium / contract</dt><dd>{money(premium * 100)}</dd></div>
            <div className="cqm-total"><dt>Total premium</dt><dd>{money(totalPremium)}</dd></div>
          </dl>
        </div>

        <div className="cqm-foot">
          <button type="button" className="cqm-btn" onClick={handleClose} disabled={isLoading}>Cancel</button>
          <button type="button" className="cqm-btn is-pri" onClick={handleConfirm} disabled={isLoading || !fillsOk}>
            {isLoading ? <><span className="cqm-spin" aria-hidden="true" />Saving…</> : (isWheelMode ? 'Sell covered call' : isRollMode ? 'Roll position' : 'Add to portfolio')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractQuantityModal;
